/**
 * The core /chat surface — the main experts UI, rebuilt on Nomad.
 *
 * Layout: a conversation list (server data via useConversations, open +
 * rename + delete) + a message thread (streamed assistant replies, per-message
 * feedback, attachment rendering) + a composer (text + file attachments).
 * Mirrors the behaviour of the static apps/experts/public/chat.{html,js},
 * talking to the same backend endpoints, but as typed React on the vendored
 * design system.
 *
 * ONE SHELL, NOT TWO (2026-10-17, Josh's "really weird double menu"). Chat used
 * to draw a second app shell inside the shared one: its own nav column with its
 * own Sign out, its own <main>, and below lg a second hamburger opening a
 * second drawer over the TopBar. The shell already owns navigation, sign-out
 * and the main landmark, so this page now:
 *   - renders inside the shell's ONE <main>, full-bleed (the route is listed in
 *     routes-registry `fullBleedPaths`), as a plain <div> — no nested <main>;
 *   - treats the conversation list as a chat-local secondary PANEL (an <aside>
 *     named "Conversations", never a <nav>), toggled from the chat header
 *     (ConversationsToggle: an icon with a Hide/Show tooltip on desktop, a
 *     visibly labelled "Conversations" control on a phone — never a second
 *     hamburger). The header's title is the current conversation
 *     (ChatHeaderTitle), so it never repeats the panel heading;
 *   - leaves sign-out to the TopBar account menu.
 *
 * Responsive (the `lg` = 1024px convention this surface already uses for the
 * artifact dock, see useMediaQuery.ts):
 *   - >= lg: the panel is an in-flow column left of the transcript, shown by
 *     default and collapsible from the Conversations control.
 *   - < lg: a single column. The panel is a sheet that slides over the chat
 *     surface ONLY (absolute inside this `relative` container, which sits below
 *     the TopBar), with a chat-local scrim. The container is `isolate`, a
 *     stacking context of its own, so nothing in chat can paint over the
 *     shell's TopBar (z-30) or nav drawer (z-40).
 *
 * Composition only — every sub-surface lives in ./components and pulls its data
 * through ./data-access hooks, so this page stays declarative.
 */
import { useRef, useState } from 'react';
import { ArtifactPanel } from './components/ArtifactPanel';
import { ChatDropZone } from './components/ChatDropZone';
import { ChatHeaderTitle } from './components/ChatHeaderTitle';
import { ConversationSidebar } from './components/ConversationSidebar';
import { ConversationsToggle } from './components/ConversationsToggle';
import { Composer } from './components/Composer';
import { MessageThread } from './components/MessageThread';
import { ShortcutsHint } from './components/ShortcutsHint';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { CHAT_WIDE_QUERY, useMediaQuery } from './hooks/useMediaQuery';
import { resolveNarrowSurface } from './narrowSurface';
import { useSessionStore } from './store/sessionStore';

export function ChatPage() {
  // Global chat shortcuts (new conversation, focus composer, stop on Esc, and
  // — when idle — Esc closes the artifacts panel).
  useKeyboardShortcuts();

  const isWide = useMediaQuery(CHAT_WIDE_QUERY);
  // The desktop column starts SHOWN and is collapsible; the phone sheet starts
  // CLOSED (the transcript is the point). Each is inert in the other layout.
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  // The sheet's open state is "the artifactOpenSeq I saw when I was opened"
  // (null = closed), so that what is RENDERED below lg is derived, in this very
  // render, from sheet + artifact together: see narrowSurface.ts for why this is
  // computed rather than kept in sync with effects.
  const [sheetOpenedAtSeq, setSheetOpenedAtSeq] = useState<number | null>(null);

  const artifactOpen = useSessionStore((s) => s.artifactPanel.open);
  const artifactOpenSeq = useSessionStore((s) => s.artifactOpenSeq);
  const closeArtifact = useSessionStore((s) => s.closeArtifact);

  // The sheet only exists below lg. Leaving that layout closes it, so narrowing
  // again later starts it CLOSED instead of resurrecting a stale open sheet.
  // Adjusting state during render (React's documented alternative to an effect
  // for this) re-renders BEFORE anything is committed.
  if (isWide && sheetOpenedAtSeq !== null) setSheetOpenedAtSeq(null);

  const narrowSurface = resolveNarrowSurface({
    isWide,
    sheetOpenedAtSeq,
    artifactOpenSeq,
    artifactOpen,
  });
  const sheetShown = narrowSurface === 'sheet';
  const panelVisible = isWide ? !panelCollapsed : sheetShown;
  // The sheet is a modal-ish layer over the chat: while it is up, everything
  // behind it (header, transcript, composer) is `inert` - out of the
  // tab order, the accessibility tree, and out of reach of wheel / touch scroll
  // (the scrim covers it too). The toggle lives in that header, so focus goes
  // back to it via this ref once the sheet closes and `inert` is lifted.
  const toggleRef = useRef<HTMLButtonElement>(null);
  const togglePanel = () => {
    if (isWide) {
      setPanelCollapsed((c) => !c);
      return;
    }
    if (sheetShown) {
      setSheetOpenedAtSeq(null);
      return;
    }
    // Opening records the current artifact seq, which supersedes any artifact
    // opened before. Closing the artifact too means it does not reappear when
    // the sheet closes: the sheet was the latest action. The seq is read from
    // the STORE at action time, not from this render's closure: an
    // openArtifact() batched in just before this click has not re-rendered us
    // yet, and a stale value would wrongly leave the artifact as the winner.
    setSheetOpenedAtSeq(useSessionStore.getState().artifactOpenSeq);
    closeArtifact();
  };

  return (
    // data-testid="chat-surface" — the chat surface root. A DIRECT child of the
    // shell's full-bleed <main> (ChatPage.layout.spec.tsx pins that).
    // The whole surface (list, conversation, artifact panel) is the file drop
    // target: a file dropped on the list must be caught, not opened by the browser.
    <ChatDropZone
      className="isolate flex h-full min-h-0 w-full flex-1 flex-row overflow-hidden"
      data-testid="chat-surface"
    >
      <ConversationSidebar
        collapsed={panelCollapsed}
        isWide={isWide}
        onNavigate={() => setSheetOpenedAtSeq(null)}
        open={sheetShown}
        returnFocusRef={toggleRef}
      />
      <div
        className="flex min-w-0 flex-1 flex-col"
        data-testid="chat-main"
        inert={sheetShown}
      >
        {/* Chat header: the panel toggle + the current conversation's title on
            the left, the discoverable shortcuts affordance on the right. */}
        <div className="flex items-center justify-between gap-2 border-border-subtle border-b px-4 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <ConversationsToggle
              buttonRef={toggleRef}
              expanded={panelVisible}
              isWide={isWide}
              onToggle={togglePanel}
            />
            <ChatHeaderTitle />
          </div>
          {/* Keyboard shortcuts mean nothing on a touch screen. */}
          <div className="hidden lg:block">
            <ShortcutsHint />
          </div>
        </div>
        <MessageThread />
        <Composer />
      </div>
      <ArtifactPanel suppressOverlay={narrowSurface === 'sheet'} />
    </ChatDropZone>
  );
}

export default ChatPage;
